import { useEffect, useRef } from 'react';
import { Intercom, onShow, shutdown, update } from '@intercom/messenger-js-sdk';
import { useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { authService } from '@/services/auth.service';
import {
  INTERCOM_APP_ID,
  intercomProfileKey,
  intercomTokenRefreshDelayMs,
  isIntercomEnabled,
} from '@/lib/intercom';

const enabled = isIntercomEnabled({
  isBuiltBundle: import.meta.env.PROD,
  forceEnabled: import.meta.env.VITE_INTERCOM_ENABLED,
});

// Sans shutdown, le cookie de session Intercom survit au logout et rouvre la conversation du compte
// précédent chez le suivant sur le même navigateur.
function shutdownIfBooted(booted: { current: boolean }) {
  if (booted.current) {
    shutdown();
    booted.current = false;
  }
}

/**
 * Charge le Messenger dans l'app authentifiée. Monté dans l'AppShell, il est donc absent des
 * pages live et spectateur, qui n'utilisent pas ce layout.
 */
export default function IntercomMessenger() {
  const userId = useAppSelector((s: RootState) => s.auth.user?.id ?? null);
  const workspaceId = useAppSelector((s: RootState) => s.workspace.currentWorkspace?.id ?? null);
  const profileKey = useAppSelector((s: RootState) => intercomProfileKey(s.auth.user));
  const booted = useRef(false);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reidentify = useRef<(() => void) | null>(null);

  // La bulle vit et meurt avec l'utilisateur : un changement de workspace ou de profil ne fait que
  // ré-émettre l'identité, sans couper la conversation en cours.
  useEffect(() => {
    if (!enabled) return;
    return () => shutdownIfBooted(booted);
  }, [userId]);

  useEffect(() => {
    if (!enabled || userId == null) return;
    // Un jeton sans workspace laisserait au contact celui du contexte précédent : pas de bulle.
    if (workspaceId == null) {
      shutdownIfBooted(booted);
      return;
    }

    let cancelled = false;
    let inFlight = false;

    const clearRefresh = () => {
      if (refreshTimer.current != null) {
        clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }
    };

    const identify = async () => {
      if (inFlight) return;
      inFlight = true;
      let issued;
      try {
        issued = await authService.intercomMessengerToken(workspaceId);
      } catch {
        inFlight = false;
        if (cancelled) return;
        // Pas d'identité signée, pas de bulle : une conversation anonyme n'est rattachable à personne.
        clearRefresh();
        shutdownIfBooted(booted);
        return;
      }
      inFlight = false;
      if (cancelled) return;

      if (booted.current) {
        update({ intercom_user_jwt: issued.token });
      } else {
        Intercom({ app_id: INTERCOM_APP_ID, intercom_user_jwt: issued.token });
        booted.current = true;
        // Le front ne connaît pas l'abonnement : on relit le contexte à chaque ouverture pour qu'un
        // changement de plan ou un renommage arrive au support sans attendre le renouvellement.
        onShow(() => reidentify.current?.());
      }

      clearRefresh();
      const delay = intercomTokenRefreshDelayMs(issued.expiresAt, Date.now());
      if (delay != null) {
        refreshTimer.current = setTimeout(() => {
          void identify();
        }, delay);
      }
    };

    reidentify.current = () => void identify();
    void identify();

    return () => {
      cancelled = true;
      reidentify.current = null;
      clearRefresh();
    };
  }, [userId, workspaceId, profileKey]);

  return null;
}
